<template>
    <gs-named-modal class="gs-refresh-controls" name="affiliate-funnel-selector-modal" :title="$t('frontend_groovesell_sub_aff_details')" width="900px"
        bg-type="regular">
        <div class="funnel-selector">
            <div v-if="activeFunnels.length" class="funnel-selector__table">
                <div id="affiliate-links-list-view">
                    <v-client-table :data="activeFunnels" :columns="funnelSelectorColumns"
                        :options="funnelSelectorTableOptions" @row-click="selectFunnel($event.row)">
                        <div slot="name" slot-scope="props">
                            <button type="button" @click.stop="selectFunnel(props.row)" class="funnel-selector__name">
                                {{ props.row.name }}
                            </button>
                        </div>
                    </v-client-table>
                </div>
            </div>
            <p v-else class="funnel-selector__empty">No earned, paid or due commissions yet.</p>
            <footer class="funnel-selector__footer" aria-label="Commission totals for all funnels">
                <p class="funnel-selector__caption">Totals across all funnels</p>
                <div v-for="total in commissionTotals" :key="total.currency" class="funnel-selector__currency">
                    <p v-if="commissionTotals.length > 1" class="funnel-selector__currency-label">{{ total.currency }}</p>
                    <dl class="funnel-selector__totals">
                        <div>
                            <dt>{{ $t('frontend_groovesell_total_earned') }}</dt>
                            <dd>{{ formatAmount(total.earned, total.currency) }}</dd>
                        </div>
                        <div>
                            <dt>{{ $t('frontend_groovesell_total_paid') }}</dt>
                            <dd>{{ formatAmount(total.paid, total.currency) }}</dd>
                        </div>
                        <div class="funnel-selector__due">
                            <dt>{{ $t('frontend_groovesell_due_comissions') }}</dt>
                            <dd>{{ formatAmount(total.due, total.currency) }}</dd>
                        </div>
                    </dl>
                </div>
            </footer>
        </div>
    </gs-named-modal>
</template>
<style scoped>
.funnel-selector { color: #1e293b; }
.funnel-selector__table { max-height: 50vh; overflow: auto; }
.funnel-selector__table ::v-deep .VueTables { width: 100%; }
.funnel-selector__table ::v-deep table { width: 100%; }
.funnel-selector__table ::v-deep th { position: sticky; top: 0; z-index: 1; background: #f7f9fc; white-space: nowrap; }
.funnel-selector__table ::v-deep td:not(:first-child) { white-space: nowrap; font-variant-numeric: tabular-nums; }
.funnel-selector__name { padding: 0; border: 0; background: transparent; color: inherit; font: inherit; text-align: left; cursor: pointer; }
.funnel-selector__name:hover { text-decoration: underline; }
.funnel-selector__name:focus-visible { outline: 2px solid #ec2478; outline-offset: 3px; }
.funnel-selector__footer { padding: 16px 20px 20px; border-top: 1px solid #e6eaf1; background: #f8fafc; }
.funnel-selector__caption { margin: 0 0 12px; color: #64748b; font-size: 12px; }
.funnel-selector__currency + .funnel-selector__currency { margin-top: 16px; }
.funnel-selector__currency-label { margin: 0 0 8px; font-size: 12px; font-weight: 600; }
.funnel-selector__totals { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; margin: 0; }
.funnel-selector__totals dt { color: #64748b; font-size: 12px; margin-bottom: 5px; }
.funnel-selector__totals dd { margin: 0; font-size: 20px; font-weight: 600; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.funnel-selector__due dd { color: #ec2478; }
.funnel-selector__empty { margin: 0; padding: 36px 20px; color: #64748b; text-align: center; }
@media (max-width: 640px) {
    .funnel-selector__footer { padding: 16px; }
    .funnel-selector__totals { gap: 12px; }
    .funnel-selector__totals dd { font-size: 16px; }
}
@media (max-width: 380px) {
    .funnel-selector__totals { grid-template-columns: 1fr; }
}
</style>
<script>
const amount = value => Number.isFinite(Number(value)) ? Number(value) : 0;

export default {
    components: {
        //
    },
    props: {
        funnels: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            funnelSelectorColumns: [
                'name',
                'human_readable_total_earned',
                'human_readable_total_paid',
                'human_readable_processed_without_advances',
            ],
            funnelSelectorTableOptions: {
                headings: {
                    name: this.$t('frontend_groovesell_funnel'),
                    human_readable_total_earned: this.$t('frontend_groovesell_total_earned'),
                    human_readable_total_paid: this.$t('frontend_groovesell_total_paid'),
                    human_readable_processed_without_advances: this.$t('frontend_groovesell_due_comissions'),
                },
                perPage: 10,
                texts: {
                    filterPlaceholder: 'Search',
                    filter: '',
                },
            },
            //
        }
    },
    computed: {
        activeFunnels() {
            return this.funnels.filter(funnel => [
                funnel.total_earned, funnel.total_paid, funnel.processed_without_advances,
            ].some(value => amount(value) !== 0));
        },
        commissionTotals() {
            // API amounts are minor units. Keep currencies separate and sum before formatting.
            const totals = new Map();
            this.activeFunnels.forEach(funnel => {
                const currency = (funnel.currency || 'USD').toUpperCase();
                const total = totals.get(currency) || { currency, earned: 0, paid: 0, due: 0 };
                total.earned += amount(funnel.total_earned);
                total.paid += amount(funnel.total_paid);
                total.due += amount(funnel.processed_without_advances);
                totals.set(currency, total);
            });
            return totals.size ? Array.from(totals.values()) : [{
                currency: ((this.funnels[0] || {}).currency || 'USD').toUpperCase(), earned: 0, paid: 0, due: 0,
            }];
        }
    },
    methods: {
        formatAmount(value, currency) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
            }).format(value / 100);
        },
        selectFunnel(funnel) {
            this.$emit('selectFunnel', funnel);
        }
    }
}
</script>
